import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon } from "semantic-ui-react";

import { InterventionSubitemsModal } from "components";
import { DMS_CAPABILITIES } from "models";
import { SubitemsTable } from "modules/AppointmentDetails/components/Interventions/components/Subitems/components/SubitemsTable";
import {
  useAddInterventionSubitemsMutation,
  useDeleteSubitemsMutation,
  useEditSubitemsMutation
} from "modules/AppointmentDetails/components/Interventions/components/Subitems/hooks";
import "modules/AppointmentDetails/components/Interventions/components/Subitems/Subitems.scss";
import { canAddSubitems } from "modules/AppointmentDetails/components/Interventions/utils";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";

import type { Intervention, InterventionElement } from "models";

type SubitemsProps = {
  intervention: Intervention;
};

export const Subitems = ({ intervention }: SubitemsProps) => {
  const { t } = useTranslation();

  const [showSubitemModal, setShowSubitemModal] = useState(false);
  const [editSubitem, setEditSubitem] = useState<InterventionElement | null>(null);

  const appointmentLocation = useAppointmentLocation();
  const { mutateAsync: addSubitemsMutation, isPending: isAddSubitemsPending } = useAddInterventionSubitemsMutation();
  const { mutateAsync: updateSubitemsMutation, isPending: isUpdateSubitemPending } = useEditSubitemsMutation();
  const { mutateAsync: deleteSubitemMutation, isPending: isDeleteSubitemPending } = useDeleteSubitemsMutation();

  const toggleSubitemModal = () => {
    setShowSubitemModal(prev => !prev);
    if (showSubitemModal) setEditSubitem(null);
  };

  const handleAddSubitem = async (element: InterventionElement) => {
    await addSubitemsMutation({
      ...element,
      intervention_id: intervention.id,
      appointment_id: intervention.appointment_id,
      is_local: !appointmentLocation?.dms_capability_ids?.includes(DMS_CAPABILITIES.CreateInterventionElement),
      vat: intervention.vat,
      labor_minutes: intervention.labor_minutes || null
    });
    toggleSubitemModal();
  };

  const handleEditSubitem = async (element: InterventionElement) => {
    if (!editSubitem) return;
    await updateSubitemsMutation({ ...editSubitem, ...element });
    setEditSubitem(null);
    setShowSubitemModal(false);
  };

  const handleDeleteSubitem = async (element: InterventionElement) => {
    if (!element.id) return;
    await deleteSubitemMutation(element.id);
  };

  const handleChooseEditRow = (element: InterventionElement) => {
    setEditSubitem(element);
    setShowSubitemModal(true);
  };

  return (
    <div className="Subitems">
      {showSubitemModal && (
        <InterventionSubitemsModal
          isLoading={isAddSubitemsPending || isUpdateSubitemPending || isDeleteSubitemPending}
          element={editSubitem}
          onSubmit={editSubitem ? handleEditSubitem : handleAddSubitem}
          onClose={toggleSubitemModal}
          location={appointmentLocation}
        />
      )}

      {intervention.elements && intervention.elements.length > 0 && (
        <SubitemsTable elements={intervention.elements} onEdit={handleChooseEditRow} onDeleteSubitem={handleDeleteSubitem} location={appointmentLocation} />
      )}

      {canAddSubitems(appointmentLocation, intervention.is_local) && (
        <Button color="green" type="button" className="left-icon mt-8" onClick={toggleSubitemModal}>
          <Icon className="plus" />
          {t("v8_subitems", "Subitems")}
        </Button>
      )}
    </div>
  );
};
